import React, { useState, useMemo } from 'react';
import { Button, Space, Alert, Tooltip } from 'antd';
import Icon, { EditOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Edit from './Edit';
import Flow from './Flow';
import { useWidget } from '../../state/dashboard';
import OpenOutIcon from '@icons/common/open-out.svg';
import RemoveWidget from '../../control/RemoveWidget';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import { useFlowConfigs } from '@src/hooks/flow/useFlowConfigs';
import {
  getFlowDrilldownMetrics,
  genFlowCompatibleDimensions,
  genFlowCompatibleFilter,
} from './utils';
import { WidgetWarning } from '../../common/WidgetWarning';
import WidgetTitle from '../../common/WidgetTitle';
import { getFirstAppliedFilter, intervalSelector } from '@src/store/common/selectors';
import EventsTooltip from './EventsTooltip';
import { dimensionsSelector } from '@src/store/trends/base/selectors';

function FlowComponent({ wid, flowId, handleEditClick }) {
  const widgetHandler = useWidget({ wid });
  const meta = widgetHandler.meta;
  const flowConfigs = useFlowConfigs();
  const currentFlow = useMemo(() => flowConfigs[flowId] || {}, [flowConfigs, flowId]);
  const appliedFilter = useSelector(getFirstAppliedFilter);
  const appliedInterval = useSelector(intervalSelector);
  const dimensions = useSelector(dimensionsSelector);
  const flowAssociatedDimensions = useMemo(
    () => new Set(currentFlow.metaDefinition?.dimensions.map((it) => it.identifier)),
    [currentFlow],
  );
  const { baseUrl } = window.conviva || { baseUrl: '' };
  const eventMode = false;

  // Function to open flow preset with filter, metrics, and dimensions data
  const openFlowPreset = () => {
    // Get compatible dimensions for this flow
    const selectedDimensions = genFlowCompatibleDimensions(dimensions, flowAssociatedDimensions);

    // Store data in localStorage instead of URL params
    const flowPresetData: {
      flowId: string;
      filter: any;
      selectedDimensions: string[];
      interval?: {
        from: string | moment.Moment;
        to: string | moment.Moment;
        shortcut: string;
        granularity?: string;
      };
    } = {
      flowId: String(currentFlow.id),
      filter: genFlowCompatibleFilter(appliedFilter, flowAssociatedDimensions),
      selectedDimensions: selectedDimensions,
    };

    // Add interval data if needed (optional)
    if (appliedInterval) {
      flowPresetData.interval = {
        from: appliedInterval.from.format ? appliedInterval.from.format() : appliedInterval.from,
        to: appliedInterval.to.format ? appliedInterval.to.format() : appliedInterval.to,
        shortcut: appliedInterval.shortcut,
        granularity: appliedInterval.granularity,
      };
    }

    // Generate a unique key for this data
    const storageKey = `flow_preset_data_${Date.now()}`;

    // Store the data in localStorage
    localStorage.setItem(storageKey, JSON.stringify(flowPresetData));

    // Navigate in the same tab to the flow preset
    const url = `${baseUrl}/dashboard/preset/flow/${String(currentFlow.id)}?flowPresetKey=${storageKey}`;
    window.location.assign(url);
  };

  const renderFlowChild = () => {
    if (eventMode) {
      return <WidgetWarning message="Not Applicable" />;
    }

    if (currentFlow.id) {
      return <Flow wid={wid} flowMeta={currentFlow} />;
    }

    return (
      <div style={{ textAlign: 'left' }}>
        <Alert type="error" message="This flow has been deleted" banner />
      </div>
    );
  };

  return (
    <WidgetCardWrapper
      wid={wid}
      title={<WidgetTitle title={currentFlow.name} meta={meta} />}
      style={{ gridRow: 'span 24' }}
      extra={
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} type="text" onClick={handleEditClick} />
          </Tooltip>
          {currentFlow.id && (
            <Tooltip title="Open flow preset">
              <Button
                size="small"
                icon={<Icon component={OpenOutIcon} />}
                type="text"
                onClick={openFlowPreset}
              />
            </Tooltip>
          )}
          <EventsTooltip currentFlow={currentFlow} />
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      {renderFlowChild()}
    </WidgetCardWrapper>
  );
}

function CreateFlowComponent({ wid, flowId, handleCancel }) {
  const widgetHandler = useWidget({ wid });
  const meta = widgetHandler.meta;
  const flowConfigs = useFlowConfigs();

  const handleSave = (newState) => {
    widgetHandler.update(newState);
    handleCancel();
  };

  return (
    <WidgetCardWrapper
      wid={wid}
      title={<WidgetTitle title={'Flow'} meta={meta} />}
      style={{ gridRow: 'span 24' }}
      extra={
        <Space>
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      <Edit
        onSave={handleSave}
        onCancel={handleCancel}
        flowConfigMeta={flowConfigs}
        currentFlowId={flowId}
      />
    </WidgetCardWrapper>
  );
}

const FlowWrapper = ({ wid }) => {
  const widgetHandler = useWidget({ wid });
  const { flowId } = widgetHandler.state;
  const [editMode, setEditMode] = useState(false);

  const handleEditClick = () => {
    setEditMode((value) => !value);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  if (!flowId || editMode) {
    return <CreateFlowComponent wid={wid} flowId={flowId} handleCancel={handleCancel} />;
  }

  return <FlowComponent wid={wid} flowId={flowId} handleEditClick={handleEditClick} />;
};

export default FlowWrapper;
